// React
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// Mantine
import { useForm } from '@mantine/form';
import {
  Button,
  Alert,
  SimpleGrid,
  Grid,
  Group,
  Radio,
  Textarea,
  TextInput,
  Flex,
  MultiSelect,
  Avatar,
  Text,
  Select,
  CheckIcon,
  useComputedColorScheme,
  AspectRatio,
  type ComboboxItem,
  type ComboboxLikeRenderOptionInput,
} from '@mantine/core';
import type { MRT_TableInstance } from 'mantine-react-table';

// Refine
import { useNotification, useTranslation } from '@refinedev/core';

// Icons
import {
  IconWand,
  IconInfoCircle,
  IconBrandCashapp,
  IconLocationBolt,
  IconDownload,
} from '@tabler/icons-react';

// Interfaces
import type { Claim, License } from 'interfaces';
import { nowISODate } from 'utils/dateUtils';
import { parseDateTime } from 'utils/dateParsing';
import {
  claimQueryReasonSchema,
  adjustmentQueryType,
  claimQueryRequiresAllServices,
  CLAIM_QUERY_COMMENT_MAX_LENGTH,
  electronicClaimQueryDataSchema,
  isSuccessfulManitobaClaimQuerySubmission,
  type ClaimQueryData,
  type ClaimQueryPaymentDetails,
} from '@billing/validation/claimQuery';
import { getClaimQueryEligibilityError } from '@billing/validation/claimQueryEligibility';
import {
  CLAIM_TARIFF_STATUS,
  type ClaimTariff,
} from '@billing/validation/tariff';

// Utils
import { buttonTextColor } from 'utils/buttonUtils';

import { useCurrentUserStore } from 'store/UserStore';

import {
  type SubmitClaimQueryRequest,
  useSubmitClaimQuery,
} from 'api/action/submit';
import { useGenerateClaimQuery } from 'api/action';
import { useUpdateClaim } from 'api/resource/claim';

import { captureException } from '@sentry/react';
import { useCurrentSiteStore } from 'store';
import { ICD9Tooltip } from 'components/common/ICD9Badge';
import { useDownloadFromSignedURL } from 'hooks/useDownloadFromSignedURL';
import { resolveClaimQueryServiceDate } from './claimQueryDates';
import {
  getClaimQueryRemittances,
  type ClaimQueryRemittance,
} from './claimQueryRemittances';

interface ClaimQueryModalContentProps {
  selectedClaim: Claim;
  onClose: () => void;
  onStepBackwards?: () => void;
  table?: MRT_TableInstance<Claim>;
}

type ClaimQueryPdfPayload = ClaimQueryData & {
  claimID: string;
  siteID: string;
  userEmail: string;
};

type ClaimQueryFormValues = Omit<
  ClaimQueryData,
  'providerLicense' | 'siteNumber' | 'providerComments' | 'queryReason'
> & {
  billingNumber: string;
  queryReason: ClaimQueryData['queryReason'] | '';
};

const MB_PAYOR_NAME = 'MB';
const QUERY_TYPE_LABELS = {
  IP: 'IP - Incorrect Patient - Drawback',
  WQ: 'WQ - WCB Query - Drawback',
  IB: 'IB - Incorrect Billing ID - Drawback',
  PR: 'PR - Provider Requested Billing Error Drawback',
  OT: 'OT - Other Query Adjustment',
  FA: 'FA - MB Finance Audit',
} as const;

const trimValue = (value?: string | null) => value?.trim() ?? '';

const getErrorDescription = (error: unknown): string | null => {
  if (!(error instanceof Error)) return null;
  return typeof error.cause === 'string' ? error.cause : error.message;
};

const getSiteBillingNumber = (claim: Claim) =>
  trimValue(
    claim.provider?.licenses?.find(
      (license) =>
        license.siteID === claim.siteID && license.payorID === claim.payorID
    )?.license
  );

const getFallbackMBBillingNumber = (claim: Claim) =>
  trimValue(
    claim.provider?.licenses?.find(
      (license: License) => license.payorName === MB_PAYOR_NAME
    )?.license
  );

const resolveProviderLicense = (claim: Claim, billingNumber: unknown) =>
  trimValue(typeof billingNumber === 'string' ? billingNumber : null) ||
  getFallbackMBBillingNumber(claim);

export const ClaimQueryModalContent: React.FC<ClaimQueryModalContentProps> = ({
  selectedClaim,
  onClose,
  onStepBackwards = undefined,
}) => {
  const { email } = useCurrentUserStore();
  const { timezone, apiID, apiSecret, apiKey } = useCurrentSiteStore();

  const computedColorScheme = useComputedColorScheme('dark');
  const color = computedColorScheme === 'dark' ? 'white' : 'black';
  const { open } = useNotification();
  const { translate } = useTranslation();

  const remittances = useMemo(
    () => getClaimQueryRemittances(selectedClaim.debits ?? [], timezone),
    [selectedClaim.debits, timezone]
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [submitting, setIsSubmitting] = useState<boolean>(false);
  const [fileUrl, setFileUrl] = useState<string>('');
  const generatedTariffIDsRef = useRef<string[]>([]);
  const initialServiceDateRef = useRef<string>('');
  const submitQuery = useSubmitClaimQuery();
  const createQueryForm = useGenerateClaimQuery();
  const updateClaim = useUpdateClaim();
  const isConfiguredForEClaims = Boolean(apiID && apiSecret && apiKey);

  const handleManualQueryDownloaded = async (): Promise<void> => {
    const queriedTariffIDs = new Set(generatedTariffIDsRef.current);
    const updatedTariffs = selectedClaim.tariffs.map((tariff) =>
      queriedTariffIDs.has(tariff.id)
        ? { ...tariff, status: CLAIM_TARIFF_STATUS.QUERIED }
        : tariff
    );
    const hasStatusChanges = updatedTariffs.some(
      (tariff, index) => tariff.status !== selectedClaim.tariffs[index]?.status
    );

    if (!hasStatusChanges) {
      onClose();
      return;
    }

    try {
      await updateClaim.mutateAsync({
        dirtyFields: { tariffs: true },
        newData: {
          ...selectedClaim,
          tariffs: updatedTariffs,
        },
      });
      onClose();
    } catch {
      return;
    }
  };

  const downloadQuery = useDownloadFromSignedURL({
    getSignedURL: async () => fileUrl,
    filename: `claim-query-${
      selectedClaim.claimNumber || selectedClaim.id
    }.pdf`,
    setLoading: setIsSubmitting,
    onSuccess: handleManualQueryDownloaded,
    onError: () =>
      open({
        type: 'error',
        message: translate(
          'pages:claims.claimQuery.downloadErrorTitle',
          'Unable to Download Claim Query'
        ),
        description: translate(
          'pages:claims.claimQuery.downloadErrorDescription',
          'The claim query PDF could not be downloaded. Please try again.'
        ),
      }),
  });

  const ignoredKeys = [
    'comment',
    'tariffs',
    'remittanceDate',
    'microfilm',
    'queryReason',
    'billingNumber',
    'queryType',
  ];

  const form = useForm<ClaimQueryFormValues>({
    initialValues: {
      lastName: '',
      firstName: '',
      dateOfBirth: '',
      sex: '',
      phin: '',
      province: '',
      provider: '',
      postalCode: '',
      microfilm: '',
      remittanceDate: '',
      claimNumber: '',
      serviceDate: '',
      tariffs: [],
      queryReason: '',
      queryType: 'OT',
      comment: '',
      billingNumber: '',
      date: nowISODate(timezone),
    },
  });

  const buildPaymentDetails = useCallback(
    (
      claimTariff: ClaimTariff,
      remittance?: ClaimQueryRemittance
    ): ClaimQueryPaymentDetails => {
      const relatedDebit = remittance?.debits.find(
        (debit) => debit.tariffID === claimTariff.id
      );
      return {
        tariffID: claimTariff.id,
        serviceCode: `${claimTariff.prefix}${claimTariff.code}`,
        serviceDate: resolveClaimQueryServiceDate({
          remittanceServiceDate: relatedDebit?.remittanceLine?.serviceDate,
          tariffServiceStart: claimTariff.serviceStart,
          claimServiceStart: selectedClaim.serviceStart,
          timezone,
        }),
        paymentDate: relatedDebit ? remittance.date : '',
        billAmount: relatedDebit?.requestedAmount || '',
        paymentAmount: relatedDebit?.paidAmount || '',
        units: String(claimTariff.units || ''),
      };
    },
    [selectedClaim, timezone]
  );

  useEffect(() => {
    if (selectedClaim) {
      const latestRemittance = remittances[0];
      const initialTariffs = (selectedClaim.tariffs || []).map((tariff) =>
        buildPaymentDetails(tariff, latestRemittance)
      );
      const initialServiceDate =
        initialTariffs[0]?.serviceDate ??
        resolveClaimQueryServiceDate({
          claimServiceStart: selectedClaim.serviceStart,
          timezone,
        });
      initialServiceDateRef.current = initialServiceDate;
      form.setValues({
        lastName: selectedClaim.patient?.lastName || '',
        firstName: selectedClaim.patient?.firstName || '',
        dateOfBirth: selectedClaim.patient?.dateOfBirth || '',
        sex: selectedClaim.patient?.sex || '',
        phin: selectedClaim.healthcard?.number || '',
        province: selectedClaim.patient?.province || '',
        provider: selectedClaim.provider
          ? `${selectedClaim.provider.firstName || ''} ${
              selectedClaim.provider.lastName || ''
            }`.trim()
          : '',
        postalCode: selectedClaim.patient?.postalCode || '',
        microfilm: latestRemittance?.microfilm ?? '',
        remittanceDate: latestRemittance?.date ?? '',
        claimNumber: selectedClaim.claimNumber || '',
        serviceDate: initialServiceDate,
        tariffs: initialTariffs, // default all tariffs selected
        queryReason: '',
        queryType: 'OT',
        comment: '',
        billingNumber: getSiteBillingNumber(selectedClaim),
        date: nowISODate(timezone),
      });
    }
  }, [buildPaymentDetails, selectedClaim, timezone, remittances]);

  const selectedRemittance = remittances.find(
    (remittance) =>
      remittance.microfilm === form.values.microfilm &&
      remittance.date === form.values.remittanceDate
  );

  const selectRemittance = (value: string | null): void => {
    const remittance = remittances.find((item) => item.value === value);
    if (!remittance) return;
    const selectedIDs = new Set(
      form.values.tariffs.map((tariff) => tariff.tariffID)
    );
    const tariffs = selectedClaim.tariffs
      .filter((tariff) => selectedIDs.has(tariff.id))
      .map((tariff) => buildPaymentDetails(tariff, remittance));
    const serviceDate =
      tariffs[0]?.serviceDate ?? initialServiceDateRef.current;
    const preserveServiceDate =
      form.values.serviceDate !== initialServiceDateRef.current;
    initialServiceDateRef.current = serviceDate;
    form.clearFieldError('tariffs');
    form.setValues({
      microfilm: remittance.microfilm,
      remittanceDate: remittance.date,
      tariffs,
      serviceDate: preserveServiceDate ? form.values.serviceDate : serviceDate,
    });
  };

  const eligibilityError = getClaimQueryEligibilityError(
    selectedClaim,
    (value) => parseDateTime(value, { zone: 'utc' }).toMillis()
  );
  const eligibilityMessages = {
    notManitoba:
      'Electronic queries are only available for Manitoba Health claims.',
    unprocessed:
      'No processed Manitoba Health remittance is recorded for this claim.',
    pending:
      'Queries cannot be submitted while any service is pending with Manitoba Health.',
    openQuery: 'This claim already has an open query.',
  };

  const eligibilityGuidance = {
    notManitoba: 'Use the query process for this claim’s payor instead.',
    pending:
      'Wait for Manitoba Health to finish processing all services and for the updated remittance to be reconciled.',
    openQuery:
      'Review the existing query on the Queries page. A new query can be sent once the open query is resolved.',
  };

  const describeSubmissionError = (error: unknown): string => {
    const description = getErrorDescription(error);
    if (description?.includes('OpenClaimQueryExistsError')) {
      return translate(
        'pages:claims.claimQuery.eligibility.openQuery',
        eligibilityMessages.openQuery
      );
    }
    const eligibility = Object.entries(eligibilityMessages).find(([code]) =>
      description?.includes(`ClaimQueryEligibilityError: ${code}`)
    );
    if (eligibility) {
      return translate(
        `pages:claims.claimQuery.eligibility.${eligibility[0]}`,
        eligibility[1]
      );
    }
    if (description?.includes('ClaimQueryRequiresAllServicesError')) {
      return translate(
        'pages:claims.claimQuery.allServices',
        'This query type includes every service in the claim.'
      );
    }
    return (
      description ??
      translate(
        'pages:claims.claimQuery.submitErrorDescription',
        'The claim query could not be submitted. Please try again.'
      )
    );
  };

  const handleElectronicQuery = async (payload: SubmitClaimQueryRequest) => {
    setIsSubmitting(true);
    try {
      const response = await submitQuery.mutateAsync(payload);
      if (!isSuccessfulManitobaClaimQuerySubmission(response)) {
        throw new Error('ErrorSubmittingQuery', {
          cause: response?.response ? response.response : response.evmessage,
        });
      }

      open({
        message: translate(
          'pages:claims.claimQuery.submitSuccessTitle',
          'Claim Query Submitted'
        ),
        description: translate(
          'pages:claims.claimQuery.submitSuccessDescription',
          'The claim query was successfully submitted to MHSC.'
        ),
        type: 'success',
      });
      onClose();
    } catch (error) {
      captureException(error);
      open({
        message: translate(
          'pages:claims.claimQuery.submitErrorTitle',
          'Error Submitting Claim Query'
        ),
        description: describeSubmissionError(error),
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormSubmit = async (values: ClaimQueryFormValues) => {
    if (submitting || isLoading) return;
    if (!trimValue(values.comment)) {
      form.setFieldError(
        'comment',
        translate('pages:claims.claimQuery.commentRequired', 'Enter a comment.')
      );
      return;
    }
    if (!values.tariffs.length) {
      form.setFieldError(
        'tariffs',
        translate(
          'pages:claims.claimQuery.tariffsRequired',
          'Select at least one service.'
        )
      );
      return;
    }
    if (
      isConfiguredForEClaims &&
      values.tariffs.some(
        (tariff) =>
          !selectedRemittance?.debits.some(
            (debit) => debit.tariffID === tariff.tariffID
          )
      )
    ) {
      form.setFieldError(
        'tariffs',
        translate(
          'pages:claims.claimQuery.remittanceServicesRequired',
          'The selected remittance does not include every selected service. Choose a matching remittance or adjust the service selection.'
        )
      );
      return;
    }
    const providerLicense = resolveProviderLicense(
      selectedClaim,
      values.billingNumber
    );
    if (!providerLicense) {
      form.setFieldError(
        'billingNumber',
        translate(
          'pages:claims.claimQuery.billingNumberRequired',
          'Select a valid Manitoba billing number.'
        )
      );
      return;
    }
    const tariffs =
      values.serviceDate !== initialServiceDateRef.current
        ? values.tariffs.map((tariff) => ({
            ...tariff,
            serviceDate: values.serviceDate,
          }))
        : values.tariffs;
    const payload = {
      ...values,
      tariffs,
      queryReason: values.queryReason || undefined,
      claimID: selectedClaim.id,
      providerLicense,
      providerComments: selectedClaim.providerComments,
      siteNumber: selectedClaim.site.siteNumber,
      siteID: selectedClaim.siteID,
    };
    if (isConfiguredForEClaims) {
      if (eligibilityError) return;
      const parsed = electronicClaimQueryDataSchema.safeParse(payload);
      if (!parsed.success) {
        open?.({
          type: 'error',
          message: translate(
            'pages:claims.claimQuery.invalidQuery',
            'Complete the required claim, service and comment fields. Comments must be 250 characters or fewer.'
          ),
        });
        return;
      }
      await handleElectronicQuery({
        ...parsed.data,
        claimID: payload.claimID,
        siteID: payload.siteID,
      });
      return;
    }
    if (!values.queryReason) {
      form.setFieldError(
        'queryReason',
        translate(
          'pages:claims.claimQuery.reasonRequired',
          'Select a query reason.'
        )
      );
      return;
    }
    setIsLoading(true);
    try {
      const pdfPayload: ClaimQueryPdfPayload = {
        ...payload,
        queryReason: values.queryReason,
        userEmail: email,
      };
      const response = await createQueryForm.mutateAsync(pdfPayload);
      if (response.statusCode !== 200)
        throw new Error('ClaimQueryPDFGenerationError');
      generatedTariffIDsRef.current = tariffs.map((tariff) => tariff.tariffID);
      setFileUrl(response.url);
    } catch {
      open?.({
        type: 'error',
        message: translate(
          'pages:claims.claimQuery.generateError',
          'Unable to generate the claim query PDF. Please try again.'
        ),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const camelCaseToTitle = (key: string) => {
    const result = key.replace(/([A-Z])/g, ' $1');
    return result.charAt(0).toUpperCase() + result.slice(1);
  };

  const renderMultiSelectOption = ({
    option,
  }: ComboboxLikeRenderOptionInput<ComboboxItem>) => {
    const tariff = selectedClaim.tariffs.find(
      (candidate) => candidate.id === option.value
    );
    const debit = selectedClaim.debits?.find(
      (candidate) => candidate.tariffID === tariff?.id
    );

    return (
      <Group gap="sm">
        <Avatar size={36} radius="xl">
          <IconBrandCashapp size={24} />
        </Avatar>
        <div>
          <Text size="sm">
            {translate('pages:claims.claimQuery.fields.tariff', 'Tariff')}:{' '}
            {tariff?.prefix}
            {tariff?.code}
          </Text>
          <Text size="xs" opacity={0.5}>
            {translate('pages:claims.claimQuery.fields.fee', 'Fee')}: $
            {debit?.requestedAmount}
          </Text>
          <Text size="xs" opacity={0.5}>
            {translate('pages:claims.claimQuery.fields.units', 'Units')}:{' '}
            {tariff?.units}
          </Text>
          {tariff?.icd9 ? (
            <ICD9Tooltip icd9={tariff.icd9}>
              <Text size="xs" opacity={0.5}>
                {translate(
                  'pages:claims.claimQuery.fields.diagnosis',
                  'Diagnosis'
                )}
                : {tariff.icd9}
              </Text>
            </ICD9Tooltip>
          ) : null}
        </div>
      </Group>
    );
  };

  const handleTariffSelection = (selected: string[]) => {
    const selectedIDs = new Set(selected);
    form.setFieldValue(
      'tariffs',
      selectedClaim.tariffs
        .filter((tariff) => selectedIDs.has(tariff.id))
        .map((tariff) => buildPaymentDetails(tariff, selectedRemittance))
    );
  };

  const handleQueryReasonChange = (value: string) => {
    const result = claimQueryReasonSchema.safeParse(value);
    if (result.success) {
      form.setFieldValue('queryReason', result.data);
    }
  };

  return (
    <>
      {fileUrl && (
        <div
          style={{
            height: '75vh', // take up 75% of viewport height
          }}
        >
          <AspectRatio ratio={4 / 3}>
            <iframe
              src={fileUrl}
              title="Claim Query"
              style={{
                border: 'none',
                height: '70vh',
              }}
            />
          </AspectRatio>
          <Flex justify={'center'} gap={'lg'} style={{ marginTop: '1%' }}>
            <Button
              variant="outline"
              radius="md"
              color="gray"
              c={buttonTextColor(computedColorScheme, false)}
              onClick={() => setFileUrl('')}
              disabled={submitting}
              data-testid="claim-query-preview-cancel"
            >
              {translate('pages:claims.claimQuery.cancel', 'Cancel')}
            </Button>
            <Button
              variant="outline"
              radius="md"
              color="green"
              c={buttonTextColor(computedColorScheme, submitting)}
              onClick={() => void downloadQuery()}
              loading={submitting}
              aria-busy={submitting}
              leftSection={<IconDownload />}
              data-testid="download-close-query"
            >
              {translate(
                'pages:claims.claimQuery.downloadAndClose',
                'Download & Close'
              )}
            </Button>
          </Flex>
        </div>
      )}
      {!fileUrl && (
        <form onSubmit={form.onSubmit(handleFormSubmit)}>
          <SimpleGrid
            cols={{ base: 1, sm: 3 }}
            w="75%"
            mx="auto"
            spacing="md"
            verticalSpacing="md"
          >
            {Object.keys(form?.getValues()).map(
              (key) =>
                !ignoredKeys.includes(key) && (
                  <TextInput
                    key={form.key(key)}
                    c={color}
                    label={translate(
                      `pages:claims.claimQuery.fields.${key}`,
                      camelCaseToTitle(key)
                    )}
                    {...form.getInputProps(key)}
                    error={
                      key !== 'postalCode'
                        ? form?.getValues()[key] === ''
                        : false
                    }
                    value={form?.getValues()[key]}
                  />
                )
            )}
            <Select
              label={translate(
                'pages:claims.claimQuery.fields.billingNumber',
                'Billing Number'
              )}
              c={color}
              error={form.errors.billingNumber}
              value={form?.getValues().billingNumber}
              data={
                selectedClaim?.provider?.licenses
                  ?.filter((license) => license.siteID === selectedClaim.siteID)
                  .map((license) => license.license) || []
              }
              onChange={(e) => {
                form.setFieldValue('billingNumber', e ?? '');
              }}
            />
            <Grid columns={3} gutter="md" style={{ gridColumn: '1 / -1' }}>
              <Grid.Col span={{ base: 3, sm: 2 }}>
                <Select
                  label={translate(
                    'pages:claims.claimQuery.fields.microfilm',
                    'Microfilm'
                  )}
                  value={selectedRemittance?.value ?? null}
                  c={color}
                  data={remittances.map(({ value, label }) => ({
                    value,
                    label,
                  }))}
                  allowDeselect={false}
                  disabled={submitting}
                  onChange={selectRemittance}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 3, sm: 1 }}>
                <TextInput
                  label={translate(
                    'pages:claims.claimQuery.fields.remittanceDate',
                    'Remittance Date'
                  )}
                  c={color}
                  value={form?.getValues().remittanceDate}
                  readOnly
                  disabled
                />
              </Grid.Col>
            </Grid>
            {isConfiguredForEClaims && (
              <Select
                style={{ gridColumn: '1 / -1' }}
                label={translate(
                  'pages:claims.claimQuery.queryType',
                  'Query Type'
                )}
                data={Object.entries(QUERY_TYPE_LABELS).map(
                  ([value, label]) => ({
                    value,
                    label: translate(
                      `pages:claims.claimQuery.types.${value}`,
                      label
                    ),
                  })
                )}
                value={form.values.queryType}
                allowDeselect={false}
                disabled={submitting}
                onChange={(value) => {
                  const parsed = adjustmentQueryType.safeParse(value);
                  if (!parsed.success) return;
                  form.setFieldValue('queryType', parsed.data);
                  if (claimQueryRequiresAllServices(parsed.data)) {
                    form.setFieldValue(
                      'tariffs',
                      selectedClaim.tariffs.map((tariff) =>
                        buildPaymentDetails(tariff, selectedRemittance)
                      )
                    );
                  }
                }}
              />
            )}
          </SimpleGrid>
          <Group justify="center" mt="md" style={{ width: '100%' }}>
            <MultiSelect
              label={translate(
                'pages:claims.claimQuery.fields.tariffs',
                'Tariffs'
              )}
              disabled={
                submitting ||
                (isConfiguredForEClaims &&
                  claimQueryRequiresAllServices(form.values.queryType))
              }
              description={
                isConfiguredForEClaims &&
                claimQueryRequiresAllServices(form.values.queryType)
                  ? translate(
                      'pages:claims.claimQuery.allServices',
                      'This query type includes every service in the claim.'
                    )
                  : undefined
              }
              error={form.errors.tariffs}
              withCheckIcon={true}
              c={color}
              hidePickedOptions={true}
              checkIconPosition="left"
              value={form.values.tariffs.map((tariff) => tariff.tariffID)}
              data={selectedClaim.tariffs.map((tariff) => {
                return {
                  value: tariff.id,
                  label: `${tariff.prefix}${tariff.code}`,
                };
              })}
              renderOption={renderMultiSelectOption}
              style={{ width: '75%' }}
              onChange={(e) => {
                handleTariffSelection(e);
              }}
            />
            {!isConfiguredForEClaims && (
              <Flex direction="column">
                <Radio.Group
                  error={form.errors.queryReason}
                  value={form?.getValues().queryReason}
                  onChange={handleQueryReasonChange}
                >
                  <Radio
                    icon={CheckIcon}
                    c={color}
                    label={translate(
                      'pages:claims.claimQuery.fields.paymentNotListed',
                      'Payment Not Listed'
                    )}
                    value="paymentNotListed"
                  />
                  <Radio
                    icon={CheckIcon}
                    c={color}
                    label={translate(
                      'pages:claims.claimQuery.fields.underpaid',
                      'Underpaid - Please Adjust'
                    )}
                    value="underpaid"
                    style={{ marginTop: '2%' }}
                  />
                  <Radio
                    icon={CheckIcon}
                    c={color}
                    label={translate(
                      'pages:claims.claimQuery.fields.overpaid',
                      'Overpaid - Please Withdraw'
                    )}
                    value="overpaid"
                    style={{ marginTop: '2%' }}
                  />
                </Radio.Group>
              </Flex>
            )}

            <Textarea
              c={color}
              label={translate(
                'pages:claims.claimQuery.fields.comment',
                'MHSC Comment'
              )}
              style={{ width: '75%' }}
              maxLength={
                isConfiguredForEClaims
                  ? CLAIM_QUERY_COMMENT_MAX_LENGTH
                  : undefined
              }
              {...form.getInputProps('comment')}
            />
          </Group>
          {isConfiguredForEClaims && eligibilityError && (
            <Alert
              color="blue"
              variant="light"
              radius="md"
              w="75%"
              mx="auto"
              mt="lg"
              title={
                <Group gap="xs" justify="center">
                  <IconInfoCircle size={18} aria-hidden="true" />
                  <Text component="span" size="sm" fw={600}>
                    {translate(
                      'pages:claims.claimQuery.eligibilityTitle',
                      'Electronic submission unavailable'
                    )}
                  </Text>
                </Group>
              }
              styles={{
                title: { justifyContent: 'center' },
                message: { textAlign: 'center' },
              }}
            >
              <Text size="sm">
                {translate(
                  `pages:claims.claimQuery.eligibility.${eligibilityError}`,
                  eligibilityMessages[eligibilityError]
                )}
              </Text>
              {eligibilityError !== 'unprocessed' && (
                <Text size="sm" mt="xs">
                  {translate(
                    `pages:claims.claimQuery.eligibilityGuidance.${eligibilityError}`,
                    eligibilityGuidance[eligibilityError]
                  )}
                </Text>
              )}
            </Alert>
          )}
          <Flex
            justify={'center'}
            gap={'lg'}
            style={{ marginTop: '3%', marginBottom: '3%' }}
          >
            <Button
              variant="outline"
              radius="md"
              color="gray"
              c={buttonTextColor(computedColorScheme, false)}
              disabled={submitting || isLoading}
              data-testid="claim-query-cancel"
              onClick={onStepBackwards ?? onClose}
            >
              {translate('pages:claims.claimQuery.cancel', 'Cancel')}
            </Button>
            <Button
              type="submit"
              variant="outline"
              radius="md"
              color="green"
              c={buttonTextColor(computedColorScheme, isLoading || submitting)}
              loading={isLoading || submitting}
              aria-busy={isLoading || submitting}
              disabled={isConfiguredForEClaims && Boolean(eligibilityError)}
              leftSection={
                isConfiguredForEClaims ? <IconLocationBolt /> : <IconWand />
              }
              data-testid="claim-query-submit"
            >
              {isConfiguredForEClaims
                ? translate('pages:claims.claimQuery.eSubmit', 'E-Submit')
                : translate('pages:claims.claimQuery.generate', 'Generate')}
            </Button>
          </Flex>
        </form>
      )}
    </>
  );
};
